/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronRight, FileSpreadsheet, FileText, Upload,
} from 'lucide-react';
import { Text, UnstyledButton } from '@/shared/ui';
import type { ExportFormat, SpreadsheetEntity } from '../types';

export interface SpreadsheetMenuPanelProps {
  entity: SpreadsheetEntity;
  totalCount: number;
  onImport: () => void;
  onExport: (format: ExportFormat) => void;
  onOpenHub: () => void;
  /**
   * Whether the current user may import. A broker may not: the backend gates
   * every import verb on `import_spreadsheet` (OPERATIONAL+), so showing the
   * entry to them would open a screen that answers 403 on upload. The export
   * half stays visible for everybody — a broker exports their own clients.
   */
  canImport: boolean;
}

/** The two file formats. Exporting into Drive was the third and is gone. */
const FORMATS: Array<{ value: ExportFormat; Icon: typeof FileSpreadsheet }> = [
  { value: 'xlsx', Icon: FileSpreadsheet },
  { value: 'csv', Icon: FileText },
];

const panelStyle: React.CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 8px)',
  right: 0,
  width: 280,
  zIndex: 20,
  background: 'var(--surface-raised)',
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--radius-lg)',
  boxShadow: 'var(--shadow-lg)',
  overflow: 'hidden',
  fontFamily: 'var(--font-family)',
  textAlign: 'left',
};

const helpStyle: React.CSSProperties = {
  fontSize: 'var(--text-small)',
  color: 'var(--text-secondary)',
  lineHeight: 1.4,
};

/** One export format tile: icon above, label below. */
const FormatTile: React.FC<{
  format: ExportFormat;
  Icon: typeof FileSpreadsheet;
  onSelect: (format: ExportFormat) => void;
}> = ({ format, Icon, onSelect }) => {
  const { t } = useTranslation('features/spreadsheets');
  const [hover, setHover] = useState(false);
  return (
    <UnstyledButton
      type="button"
      data-testid={`spreadsheet-export-${format}`}
      onClick={() => onSelect(format)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        gap: 'var(--space-2)', padding: '10px var(--space-1)',
        borderRadius: 'var(--radius-md)',
        border: `1px solid ${hover ? 'var(--seed-base)' : 'var(--border-subtle)'}`,
        color: hover ? 'var(--seed-base)' : 'var(--text-primary)',
        background: 'transparent', cursor: 'pointer', textAlign: 'center',
        fontSize: 'var(--text-small)', fontWeight: 'var(--font-semibold)' as unknown as number,
        whiteSpace: 'nowrap',
      }}
    >
      <Icon width={18} height={18} style={{ color: 'var(--accent-salvia)' }} />
      {t(`menu.formats.${format}`)}
    </UnstyledButton>
  );
};

/**
 * The menu itself: import, export, and the way through to the Planilhas area.
 *
 * @param props - Entity, list size, and the three actions.
 * @returns The rendered panel.
 */
export const SpreadsheetMenuPanel: React.FC<SpreadsheetMenuPanelProps> = ({
  entity, totalCount, onImport, onExport, onOpenHub, canImport,
}) => {
  const { t } = useTranslation('features/spreadsheets');
  const exportHelpKey = entity === 'clients' ? 'menu.exportHelpClients' : 'menu.exportHelpProperties';

  return (
    <div role="menu" data-testid="spreadsheet-menu" style={panelStyle}>
      {canImport && (
      <UnstyledButton
        type="button"
        role="menuitem"
        data-testid="spreadsheet-menu-import"
        onClick={onImport}
        style={{
          display: 'flex', alignItems: 'flex-start', gap: 10, width: '100%',
          padding: 'var(--space-3) 14px', cursor: 'pointer', background: 'transparent',
          textAlign: 'left',
        }}
      >
        <Upload width={16} height={16} style={{ flexShrink: 0, marginTop: 2, color: 'var(--seed-base)' }} />
        <Text component="span" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Text as="span" style={{ fontSize: 'var(--text-body-sm)', fontWeight: 'var(--font-semibold)' as unknown as number, whiteSpace: 'nowrap' }}>
            {t('menu.importTitle')}
          </Text>
          <Text as="span" style={helpStyle}>{t('menu.importHelp')}</Text>
        </Text>
      </UnstyledButton>
      )}

      <div style={{
        borderTop: '1px solid var(--border-subtle)',
        background: 'var(--surface-tertiary)',
        padding: 'var(--space-3) 14px 14px',
        display: 'flex', flexDirection: 'column', gap: 10,
      }}>
        <Text component="span" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Text as="span" style={{ fontSize: 'var(--text-body-sm)', fontWeight: 'var(--font-semibold)' as unknown as number }}>
            {t('menu.exportTitle')}
          </Text>
          <Text as="span" style={helpStyle}>{t(exportHelpKey, { count: totalCount })}</Text>
        </Text>
        <div style={{
          display: 'grid',
          // Two tiles, not three left-aligned with a hole where Sheets was.
          gridTemplateColumns: `repeat(${FORMATS.length}, minmax(0, 1fr))`,
          gap: 6,
        }}>
          {FORMATS.map(({ value, Icon }) => (
            <FormatTile key={value} format={value} Icon={Icon} onSelect={onExport} />
          ))}
        </div>
      </div>

      {canImport && (
      <UnstyledButton
        type="button"
        role="menuitem"
        data-testid="spreadsheet-menu-hub"
        onClick={onOpenHub}
        style={{
          borderTop: '1px solid var(--border-ultra-subtle)',
          padding: '10px 14px', width: '100%',
          fontSize: 'var(--text-body-sm)', color: 'var(--text-secondary)',
          cursor: 'pointer', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', background: 'transparent', whiteSpace: 'nowrap',
        }}
      >
        {t('menu.hubLink')}
        <ChevronRight width={14} height={14} />
      </UnstyledButton>
      )}
    </div>
  );
};
